<template>
  <div id="app-root">
    <!-- Non-authenticated view (Login) -->
    <template v-if="isAuthPage">
      <router-view></router-view>
    </template>

    <!-- Authenticated layout shell -->
    <template v-else>
      <div class="layout-container">
        <div style="z-index: 1000 !important">
          <SideNavbar
            ref="sideNavbar"
            @button-click="handleLangChange"
          ></SideNavbar>
        </div>

        <!-- Main Flex Column: Header + Scrollable Content -->
        <div class="page-main-column">
          <!-- Fixed Header -->
          <header class="sticky-header">
            <div class="header-content">
              <div class="header-left-section">
                <div class="toggle-btn-container">
                  <div
                    class="toggle-btn"
                    :class="{ 'is-active': !isSidebarCollapsed }"
                    @click="toggleSidebar"
                    title="Toggle Sidebar"
                  >
                    <i class="bx bx-sidebar"></i>
                  </div>
                </div>
                <div class="page-title-container">
                  <h2 class="page-title">{{ currentPageTitle }}</h2>
                </div>
              </div>
            </div>
          </header>

          <!-- Scrollable Content Area -->
          <main class="page-scroll-content">
            <!-- Operational notices sit above the page content, on every route, instead of the
                 blocking modal the dashboard used to raise on each login. -->
            <SystemNotices />
            <router-view v-slot="{ Component }">
              <transition name="fade" mode="out-in">
                <component 
                  :is="Component" 
                  :key="$route.fullPath"
                  @lang-change="handleLangChange"
                />
              </transition>
            </router-view>
          </main>
        </div>

        <BottomFooter ref="BottomFooterRef"></BottomFooter>
      </div>
    </template>
  </div>
</template>

<script>
import SideNavbar from "./components/SideNavbar.vue";
import BottomFooter from "./components/BottomFooter.vue";
import SystemNotices from "./components/SystemNotices.vue";

export default {
  name: "App",
  components: {
    SideNavbar,
    BottomFooter,
    SystemNotices,
  },
  data() {
    return {
      isSidebarCollapsed: false,
    };
  },
  computed: {
    isAuthPage() {
      // Don't show sidebar/header on login page
      return this.$route.path === "/" || this.$route.path === "";
    },
    currentPageTitle() {
      const path = this.$route.path;
      // Routes may carry their own title (used by the local-only simulation pages).
      if (this.$route.meta && this.$route.meta.title) return this.$route.meta.title;
      const titleMap = {
        "/dashboard": "Dashboard",
        "/enroll": "Register User",
        "/device-list": "Devices",
        "/device-details": "Device Details",
        "/device-lost-mode": "Lost Mode",
        "/register/device": "Register Device",
        "/register/user": "Enroll User",
        "/manage-user-admin": "Manage Accounts",
        "/manage-templates": "Manage Templates",
        "/reporting/audit-trail": "Audit Trail",
        "/reporting-v2": "Reporting",
        "/faq": "Help Center",
        "/settings": "Settings",
        "/profile": "User Profile",
        "/edit-account": "Edit Account",
        "/credit-manager": "Credit System",
        "/abm-connection": "ABM Connection",
      };
      
      for (const [route, title] of Object.entries(titleMap)) {
        if (path === route || path.startsWith(route + "/")) return title;
      }
      return "Ctrlytics";
    }
  },
  watch: {
    // Sync sidebar state on mount of App or on navigation
    $route() {
      this.syncSidebarState();
    }
  },
  mounted() {
    this.syncSidebarState();
  },
  methods: {
    syncSidebarState() {
      this.$nextTick(() => {
        if (this.$refs.sideNavbar) {
          this.isSidebarCollapsed = this.$refs.sideNavbar.isCollapsed;
        }
      });
    },
    handleLangChange(lang) {
      // Update footer
      if (this.$refs.BottomFooterRef) {
        this.$refs.BottomFooterRef.langchange(lang);
      }
      // Note: Components inside router-view are responsible for their own lang re-fetch
      // usually triggered by listening to the lang-change emit from the component instance
    },
    toggleSidebar() {
      if (this.$refs.sideNavbar) {
        this.$refs.sideNavbar.toggleSidebar();
        this.isSidebarCollapsed = this.$refs.sideNavbar.isCollapsed;
      }
    },
  },
};
</script>

<style>
/* Shared search field — one look for every page (Manage Devices, Manage Accounts,
   Audit Trail, ...). Rectangular with the app's standard radius, icon inset left. */
.ctrl_search_group {
  position: relative;
  display: flex;
  align-items: center;
}
.ctrl_search_icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: var(--text-muted);
  pointer-events: none;
}
.ctrl_search_input {
  appearance: none;
  padding: 9px 34px 9px 38px; /* right padding clears the × button */
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md, 8px);
  font-family: var(--font-family-base);
  font-size: 14px;
  color: var(--text-main);
  background-color: var(--surface-color);
  width: 300px;
  max-width: 100%;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ctrl_search_input::placeholder {
  color: var(--text-muted);
}
.ctrl_search_input:focus {
  outline: none;
  /* --primary-ink, not brand yellow: the focus indicator must clear 3:1 on white. */
  border-color: var(--primary-ink);
  box-shadow: 0 0 0 3px rgba(255, 214, 0, 0.25);
}
.ctrl_search_clear {
  position: absolute;
  right: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 50%;
  font-size: 18px;
  line-height: 1;
  color: var(--text-muted);
  cursor: pointer;
}
.ctrl_search_clear:hover,
.ctrl_search_clear:focus-visible {
  color: var(--text-main);
  background: var(--surface-hover);
  outline: none;
}
@media (max-width: 768px) {
  .ctrl_search_group,
  .ctrl_search_input {
    width: 100%;
  }
}

/* Visually-hidden utility for screen-reader-only labels. */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Global Layout Styles (moved from PageLayout) */
.layout-container {
  display: flex;
  background-color: var(--background-color);
  height: 100vh;
  position: relative;
  overflow: hidden;
}

.page-main-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: relative;
  overflow: hidden;
}

.sticky-header {
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 20px 40px;
  border-bottom: 1px solid var(--border-color);
  z-index: 999;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-main);
  margin: 0;
  font-family: var(--font-family-header, "Century Gothic Bold");
}

.toggle-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 26px;
  color: var(--text-main);
  transition: color 0.2s ease;
}

.toggle-btn:hover {
  color: var(--primary-ink);
}

.page-scroll-content {
  flex: 1;
  padding: 24px 40px 60px 40px;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  overflow-y: auto;
  position: relative;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .sticky-header { padding: 16px; }
  .page-scroll-content { padding: 15px; padding-bottom: 60px; }
  .page-title { font-size: 18px; }
}
</style>

